import React, { useEffect, useState } from 'react';
import ProjectCard from '../components/ProjectCard';
import { Link } from 'react-router-dom';
import { Project, PageContent } from '../types';
import LightRays from '../components/LightRays';
import GradualBlur from '../components/GradualBlur';
import { ContentService } from '../services/content';
interface HomeProps {
  projects: Project[];
  isLoading: boolean;
}
const Home: React.FC<HomeProps> = ({
  projects,
  isLoading
}) => {
  const [pageData, setPageData] = useState<PageContent | null>(null);
  const featuredLimit = 8;
  useEffect(() => {
    ContentService.getPageContent('home').then(setPageData);
  }, []);
  const heroTitle = pageData?.hero_title || 'Creation is my \ndopamine dealer 😌✨';
  const heroSubtitle = pageData?.hero_subtitle || 'So I committed up for the 100 Builds challenge to push my limits, build like crazy, and evolve into the "new me" one project at a time.';
  return <div className="max-w-screen-2xl mx-auto px-6 md:px-12 pt-[80px] md:pt-32 pb-12 md:pb-24 relative z-10" data-id="element-204">
      <section className="relative mb-12 md:mb-48 min-h-[70vh] md:min-h-[80vh] flex flex-col justify-center" data-id="element-205">
        {/* Full-bleed background container */}
        <div className="absolute inset-0 -top-[80px] md:-top-32 left-1/2 right-1/2 -translate-x-1/2 w-screen pointer-events-none z-0" style={{
        height: 'calc(100% + 80px)'
      }} data-id="element-206">
          <div className="w-full h-full relative" data-id="element-207">
            <LightRays raysOrigin="top-center" raysColor="#32cd92" raysSpeed={1.8} lightSpread={1.1} rayLength={3} followMouse={true} mouseInfluence={0.1} noiseAmount={0} distortion={0} pulsating={false} fadeDistance={1} saturation={1.6} className="custom-rays" data-id="element-208" />
          </div>

          {/* Subtle gradual blur at the bottom of the hero to blend with content */}
          <GradualBlur target="parent" position="bottom" height="15rem" strength={4} divCount={10} curve="bezier" exponential opacity={1} zIndex={1} data-id="element-209" />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black z-[2]" data-id="element-210" />
        </div>

        <div className="relative z-10 py-6 md:py-24 space-y-6 md:space-y-16" data-id="element-211">
          <div className="flex items-center space-x-2" data-id="element-212">
            <div className="flex items-center bg-white/10 backdrop-blur-xl border border-white/10 rounded-full px-4 py-1.5" data-id="element-213">
              <span className="text-[10px] text-white uppercase tracking-[0.2em] font-bold" data-id="element-214">
                {pageData?.subtitle || "Hey, I'm Shylash"}
              </span>
            </div>
          </div>
          <h1 className="text-5xl md:text-8xl lg:text-[8.5rem] font-extrabold leading-[0.95] tracking-tighter max-w-7xl text-white drop-shadow-2xl whitespace-pre-line" data-id="element-215">
            {heroTitle}
          </h1>
          <p className="text-2xl md:text-3xl text-zinc-300 leading-snug tracking-tight max-w-4xl drop-shadow-md font-[400]" data-id="element-216">
            {"I committed to the 100 Builds challenge to push my limits, build like crazy, and evolve into the 'new me'."}
          </p>

          <div className="flex flex-wrap gap-4 md:gap-6 pt-4" data-id="element-217">
            <Link to="/projects" className="bg-white text-black px-10 py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-zinc-200 transition-all cursor-target min-w-[180px] text-center" data-id="element-218">
              explore builds
            </Link>
            <Link to="/about" className="border border-white/20 backdrop-blur-sm px-10 py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-white/10 transition-all cursor-target min-w-[180px] text-center" data-id="element-219">
              about me
            </Link>
          </div>
        </div>
      </section>

      <section id="projects" className="mb-12 md:mb-48 scroll-mt-32 relative z-20" data-id="element-220">
        <div className="flex items-center justify-between mb-12 md:mb-24" data-id="element-221">
          <span className="text-[10px] text-zinc-500 uppercase tracking-[0.3em] font-extrabold" data-id="element-222">
            .featured builds
          </span>
          <div className="h-px flex-grow bg-white/10 mx-6 md:mx-12" data-id="element-223"></div>
        </div>

        {isLoading ? <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12 md:gap-y-24 animate-pulse" data-id="element-224">
            {[1, 2, 3, 4].map(i => <div key={i} className="w-full aspect-video bg-zinc-900 rounded-sm" data-id="element-225" />)}
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12 md:gap-y-32" data-id="element-226">
            {projects.slice(0, featuredLimit).map(project => <ProjectCard key={project.id} project={project} data-id="element-227" />)}
          </div>}

        <div className="flex justify-center mt-12 md:mt-48" data-id="element-228">
          <Link to="/projects" className="group relative border border-white/20 px-16 py-6 rounded-full text-xs font-bold uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-all overflow-hidden cursor-target" data-id="element-229">
            <span className="relative z-10" data-id="element-230">
              view all {isLoading ? '...' : projects.length} projects
            </span>
            <div className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-500" data-id="element-231"></div>
          </Link>
        </div>
      </section>
    </div>;
};
export default Home;